/**
 * Ticket Lifecycle v2 — Slice 5D-1
 *
 * Lists shop members eligible to hold FOH custody, for the
 * "Reassign Advisor" picker. Returns active memberships whose role is in
 * the FOH-capable set per spec §6.1, named through the canonical person-name
 * rule (src/lib/people/personDisplayName.ts) — never an email.
 *
 * The rows used to embed `profiles(display_name, email)`. `memberships`
 * has no foreign key from user_id to profiles; the only one is `removed_by`,
 * so PostgREST silently embedded the profile of whoever REMOVED the
 * membership — null for every active member — and every advisor rendered as
 * "Unknown member" even when a name existed.
 *
 * Role set (intersection of edge-function-allowed and product-appropriate
 * for advisor work):
 *   - OWNER, MANAGER, ORG_ADMIN, SERVICE_WRITER
 *
 * TECHNICIAN is allowed by the edge function role gate (spec §6.1) but
 * intentionally excluded here — this picker is for explicit *advisor*
 * reassignment, not back-of-house actor selection. PARTS / AUDITOR /
 * DISPATCHER are excluded because they cannot subsequently
 * `record-advisor-action` (whose role gate is the same five-role set as
 * `transition-ticket-custody`, minus TECHNICIAN in product intent).
 *
 * Gated by `custodyHandoff` — when off, the query is `enabled: false`.
 *
 * Boundary: this hook touches `memberships` + `profiles` only. It does
 * NOT import scheduling, timer, parking, rollover, bay, or invariants
 * code (per the lifecycle-v2 runtime boundary guard).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { actsAsLegacyRole } from '@/lib/roles/multiRole';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { fetchPersonDisplayNames, UNKNOWN_MEMBER_LABEL } from '@/lib/people/personDisplayName';

export type FohCapableRole =
  | 'OWNER'
  | 'MANAGER'
  | 'ORG_ADMIN'
  | 'SERVICE_WRITER';

/**
 * Closed allow-list of roles surfaced in the reassign picker. Mirrors
 * spec §6.1 minus TECHNICIAN (per slice 5D-1 directive — explicit
 * advisor reassignment is not a tech-to-tech action).
 */
export const FOH_CAPABLE_ROLES: ReadonlyArray<FohCapableRole> = [
  'OWNER',
  'MANAGER',
  'ORG_ADMIN',
  'SERVICE_WRITER',
];

export interface FohCapableMember {
  userId: string;
  role: FohCapableRole;
  displayName: string;
}

interface RawMembershipRow {
  user_id: string;
  role: string;
}

/**
 * Map a raw memberships row + its resolved real name (or null) to the
 * display shape. Pure. "Unknown member" only when OptiBay holds no real name
 * for the person — never an email.
 */
export function toFohCapableMember(row: RawMembershipRow, resolvedName?: string | null): FohCapableMember | null {
  if (!FOH_CAPABLE_ROLES.includes(row.role as FohCapableRole)) return null;
  const displayName = resolvedName?.trim() || UNKNOWN_MEMBER_LABEL;
  return {
    userId: row.user_id,
    role: row.role as FohCapableRole,
    displayName,
  };
}

interface UseFohCapableMembersArgs {
  shopId: string | null | undefined;
}

export function useFohCapableMembers({ shopId }: UseFohCapableMembersArgs) {
  const custodyHandoffEnabled = useFeatureFlag('custodyHandoff');
  const enabled = custodyHandoffEnabled && !!shopId;

  return useQuery({
    queryKey: ['foh-capable-members', shopId],
    enabled,
    retry: false,
    queryFn: async (): Promise<FohCapableMember[]> => {
      // ── WHY THIS NO LONGER FILTERS IN THE QUERY ──────────────────────
      //
      // `.in('role', FOH_CAPABLE_ROLES)` asked the legacy scalar "are you an
      // advisor?". One person can now hold several work roles, and the scalar
      // keeps only ONE of them — a technician who also writes repair orders
      // carries `role = 'TECHNICIAN'`, because that is the value that ADDS a
      // restriction and is therefore the safe one to keep. So the query
      // silently dropped exactly the people this picker exists to find.
      //
      // The set is now decided by `actsAsLegacyRole`, the same projection
      // `usePermissions` uses, over the membership AND its work roles. The
      // filter moves to TypeScript because the question cannot be asked of
      // one column.
      const { data, error } = await (supabase as any)
        .from('memberships')
        .select('id, user_id, role, authority_role')
        .eq('shop_id', shopId)
        .eq('status', 'active')
        .order('role');
      if (error) {
        console.error('[foh-capable-members] query failed', error);
        throw error;
      }

      const raw = (data ?? []) as Array<RawMembershipRow & {
        id?: string;
        authority_role?: string | null;
      }>;

      // Work roles for the whole shop in one read. A FAILED read is not an
      // empty set: falling through to "nobody has work roles" would hand back
      // the legacy answer silently, which is the behaviour being replaced.
      const ids = raw.map((r) => r.id).filter((x): x is string => !!x);
      let workRolesByMembership = new Map<string, string[]>();
      if (ids.length > 0) {
        const { data: wr, error: wrError } = await (supabase as any)
          .from('membership_work_roles')
          .select('membership_id, work_role')
          .in('membership_id', ids);
        if (wrError) {
          console.error('[foh-capable-members] work-role query failed', wrError);
          throw wrError;
        }
        workRolesByMembership = ((wr ?? []) as Array<{ membership_id: string; work_role: string }>)
          .reduce((m, row) => {
            const list = m.get(row.membership_id) ?? [];
            list.push(row.work_role);
            m.set(row.membership_id, list);
            return m;
          }, new Map<string, string[]>());
      }

      const names = await fetchPersonDisplayNames(supabase, raw.map((r) => r.user_id), shopId);

      const members: FohCapableMember[] = [];
      for (const row of raw) {
        const acts = actsAsLegacyRole(
          {
            role: row.role,
            authority_role: row.authority_role ?? null,
            work_roles: row.id ? (workRolesByMembership.get(row.id) ?? []) : null,
          } as never,
          ...FOH_CAPABLE_ROLES,
        );
        if (!acts) continue;

        // The row still reports ONE legacy role, because that is what this
        // picker displays and sorts by. It is the strongest FOH role the
        // person actually acts as, not whatever the scalar happened to hold.
        const displayRole =
          FOH_CAPABLE_ROLES.find((r) =>
            actsAsLegacyRole(
              {
                role: row.role,
                authority_role: row.authority_role ?? null,
                work_roles: row.id ? (workRolesByMembership.get(row.id) ?? []) : null,
              } as never,
              r,
            ),
          ) ?? null;
        if (!displayRole) continue;

        const mapped = toFohCapableMember({ user_id: row.user_id, role: displayRole }, names[row.user_id] ?? null);
        if (mapped) members.push(mapped);
      }
      // Stable display order: role first (DB already sorted), then
      // display name within role for human-friendly scanning.
      members.sort((a, b) => {
        const roleCmp =
          FOH_CAPABLE_ROLES.indexOf(a.role) -
          FOH_CAPABLE_ROLES.indexOf(b.role);
        if (roleCmp !== 0) return roleCmp;
        return a.displayName.localeCompare(b.displayName);
      });
      return members;
    },
  });
}
